import React from 'react';
import { ErrorBoundary as ErrorBoundaryBase, withErrorBoundary, useErrorBoundary } from 'react-error-boundary';
import * as Sentry from '@sentry/browser';
import Text from "./Text.js";
import Flex from "./Flex.js";
import Button from "./Button.js";
import Theme from "./Theme.js";
import S from './ErrorBoundary.css';

/**
 * @class ErrorBoundary catches JS errors thrown in its children within the React lifecycle,
 * and attempts to recover from them automatically. Error Boundaries allow you to handle errors
 * more gracefully, either recovering automatically or showing
 * a fallback component in an unrecoverable case (e.g. a crash loop),
 * rather than seeing a white screen as the whole component tree unmounts.
 * This element provides a default FallbackComponent. You can replace it with your own.
 * Just pass a component to the FallbackComponent prop.
 *
 * This HOC essentially extends the 'react-error-bounary' library's ErrorBoundary.
 * It adds to this base component the ability to reset the error state as an effect of catching an error,
 * provided that the caught error is not equal to the last caught error,
 * which would indicate a crash cycle (automatically resetting would result in a crash <> setState loop).
 *
 * @param {element} children This is what you want rendered when everything's working fine.
 * If there's an error that React can handle within the children of the ErrorBoundary,
 * the ErrorBoundary will catch that and allow you to handle it gracefully.
 * @param {element} FallbackComponent This is a component you want rendered in the event of an error.
 * As props it will be passed the error and resetErrorBoundary
 * (which will reset the error boundary's state when called,
 * useful for a "try again" button when used in combination with the onReset prop).
 * This is required if no fallback or fallbackRender prop is provided.
 * @param {any} eventData This will be supplied to the onError, onReset( evt ) function as evt.data
 * @param {function} fallbackRenderer This is a render-prop based API that allows you to inline your error fallback UI
 * into the component that's using the ErrorBoundary.
 * This is useful if you need access to something that's in the scope of the component you're using.
 * @param {element} fallback In the spirit of consistency with the React.Suspense component,
 * we also support a simple fallback prop which you can use for a generic fallback. You can render static JSX using this prop.
 * @param {function} onError This will be called when there's been an error that the ErrorBoundary has handled.
 * It will be called with one argument, and event containing { error, data : eventData, info : componentStack }.
 * @param {function} onReset This will be called immediately before the ErrorBoundary resets it's internal state (which will result in rendering the children again).
 * You should use this to ensure that re-rendering the children will not result in a repeat of the same error happening again. If this returns a Promise, the
 * reset will be called on completion of the promise
 * @param {array} resetKeys Sometimes an error happens as a result of local state to the component that's rendering the error.
 * If this is the case, then you can pass resetKeys which is an array of values.
 * If the ErrorBoundary is in an error state, then it will check these values each render and if they change
 * from one render to the next, then it will reset automatically (triggering a re-render of the children).
 * @param {function} onResetKeysChange This is called when the resetKeys are changed (triggering a reset of the ErrorBoundary).
 * It's called with the prevResetKeys and the resetKeys.
 *
 * @static @property {function} ErrorBoundary.withErrorBoundary A higher order component wrapper passed on directly from the react-error-boundary library
 * @static @property {function} ErrorBoundary.useErrorHandler A hook passed on directly from the react-error-boundary library.
 * You can use this hook to intercept errors that Error Boundaries normally don't catch, and redirect them to your boundary.
 *
 * * * Recommended Reading * * *
 * @see {@link https://reactjs.org/docs/error-boundaries.html}
 * @see {@link https://kentcdodds.com/blog/use-react-error-boundary-to-handle-errors-in-react}
 * @see {@link https://github.com/bvaughn/react-error-boundary}
 */
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.ref = /*#__PURE__*/React.createRef();
    this.state = {
      boundaryKey: 0,
      previousError: null
    };
  }
  static defaultProps = {
    FallbackComponent: DefaultErrorFallback,
    sentry: true
  };
  static withErrorBoundary = withErrorBoundary;
  static useErrorBoundary = useErrorBoundary;
  resetErrorBoundary = error => {
    const {
      boundaryKey
    } = this.state;
    this.setState({
      boundaryKey: boundaryKey + 1,
      previousError: error
    });
  };
  handleError = ({
    error
  }) => {
    const {
      previousError
    } = this.state;
    if (!previousError || previousError.message !== error.message) {
      this.resetErrorBoundary(error);
    }
  };
  onError = (error, info) => {
    const {
      onError = this.handleError,
      eventData,
      sentry
    } = this.props;
    if (sentry) {
      Sentry.captureException(error);
    }
    if (typeof onError === 'function') {
      onError({
        error,
        data: eventData,
        info
      });
    }
  };
  onReset = e => {
    const {
      onReset,
      eventData
    } = this.props;
    if (onReset) {
      const evt = {
        ...(e || {}),
        data: eventData
      };
      const p = onReset(evt);
      if (p && typeof p.then === 'function') {
        p.then(() => {
          if (this.ref.current) {
            this.ref.current.reset();
          }
        });
      }
    }
  };
  render() {
    const {
      boundaryKey
    } = this.state;
    const {
      children,
      eventData,
      onReset,
      ...rest
    } = this.props;
    return /*#__PURE__*/_jsx(ErrorBoundaryBase, {
      ref: this.ref,
      ...rest,
      onError: this.onError,
      onReset: this.onReset,
      children: children
    }, boundaryKey);
  }
}
export default ErrorBoundary;
function DefaultErrorFallback(props) {
  const {
    error,
    resetErrorBoundary
  } = props;
  const {
    t
  } = Theme.I18N.ns('ErrorBoundry');
  return /*#__PURE__*/_jsxs(Flex, {
    direction: "column",
    variant: "errorFallback",
    role: "alert",
    children: [/*#__PURE__*/_jsx(Text, {
      className: S.textField,
      variant: "errorFallbackTitle|title",
      children: t('title', 'Oops! Something went wrong.')
    }), /*#__PURE__*/_jsx(Text, {
      className: S.textField,
      variant: "errorFallbackSubtitle",
      children: t('subtitle', 'We\'re working on it. Here\'s what we know so far:')
    }), /*#__PURE__*/_jsx(Text, {
      className: S.textField,
      variant: "errorFallbackMessage",
      children: t('message', `Cause: ${error.message}`, {
        message: error.message
      })
    }), /*#__PURE__*/_jsx(Flex, {
      children: /*#__PURE__*/_jsx(Button, {
        className: S.button,
        variant: "errorRecovery",
        onClick: resetErrorBoundary,
        children: t('button')
      })
    })]
  });
}